import { CheckCircle2, XCircle, X } from 'lucide-react';
import type { Toast } from '@/components/useToast';

// ─── Container ────────────────────────────────────────────────────────────────

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none"
    >
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// ─── Item ─────────────────────────────────────────────────────────────────────

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const isSuccess = toast.variant === 'success';

  const containerCls = isSuccess
    ? 'bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700'
    : 'bg-red-50 border-red-200 dark:bg-red-900/30 dark:border-red-700';

  const iconCls = isSuccess
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';

  const textCls = isSuccess
    ? 'text-green-800 dark:text-green-200'
    : 'text-red-800 dark:text-red-200';

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-3 rounded-lg border px-4 py-3 shadow-lg ${containerCls}`}
    >
      <span className={`shrink-0 mt-0.5 ${iconCls}`} aria-hidden="true">
        {isSuccess
          ? <CheckCircle2 className="w-5 h-5" />
          : <XCircle className="w-5 h-5" />
        }
      </span>
      <p className={`flex-1 text-sm font-medium ${textCls}`}>{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        aria-label="Fechar notificação"
        className={`shrink-0 -mr-1 -mt-0.5 p-1 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors ${iconCls}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
